import React from "react";
import PropTypes from "prop-types";
import { getLocale } from "@devforce/trailhead-client-locale";
import formatDate from "lib/formatDate";
import Breadcrumb from "./Breadcrumb";
import StatsBar from "./StatsBar";
import FeedbackCard from "./FeedbackCard";
import RewardConfetti from "components/challenge/RewardConfetti";
import { buildScopeTranslate } from "lib/I18n";
import { refPropType } from "../utils";

const tResults = buildScopeTranslate("views.content.agentforce.assessments.pages.results");
const tLabels = buildScopeTranslate("views.content.agentforce.assessments.labels");
const tQuestion = buildScopeTranslate("views.content.agentforce.assessments.pages.question");

const ResultsScreen = ({
  attempt,
  canRetake,
  onRetake,
  onBackToTrail,
  onInfoClick,
  onFeedbackClick = null,
  showFeedbackButton = false,
  headingRef = null,
}) => {
  if (!attempt || !attempt.results) {
    return null;
  }

  const { results, topic } = attempt;
  const score = Math.round(results.overallScore);
  const questionCount = attempt.questions?.length || 0;
  const passingScore = Math.round(topic?.scoreThreshold || 80);
  const isScoreThresholdRequired = topic?.scoreThresholdRequired || false;
  const completedDate = results.finishedAt
    ? formatDate(results.finishedAt, { month: 'numeric' }, getLocale())
    : null;

  // Determine score icon and color based on threshold type and whether it was met
  const scoreIcon = results.scoreThresholdMet ? 'success' : 'clear';
  let scoreColor;
  if (results.scoreThresholdMet) {
    scoreColor = '#2E844A'; // Green for met
  } else if (isScoreThresholdRequired) {
    scoreColor = '#BA0517'; // Red for required not met
  } else {
    scoreColor = '#A96404'; // Orange 50 — minimum contrast (4.5:1) on white bg (W-21613772)
  }

  return (
    <>
      {results.scoreThresholdMet && <RewardConfetti colorScheme="fiesta" />}
      <Breadcrumb showProgress={false} onBackClick={onBackToTrail} onInfoClick={onInfoClick} onFeedbackClick={onFeedbackClick} showFeedbackButton={showFeedbackButton} />

      {/* Pass/fail announcement for screen readers */}
      <div aria-live="assertive" className="th-sr-only" data-testid="results-announcement">
        {results.scoreThresholdMet
          ? tResults('pass_announcement', { score })
          : tResults('fail_announcement', { score, required: passingScore })}
      </div>

      {/* Header Section */}
      <div className="th-agentforce-assessment__header-section">
        <div className="slds-container_center slds-container_medium slds-p-bottom_large th-agentforce-assessment__header-content">
          <div className="slds-text-align_center">

            <h1
              ref={headingRef}
              className="slds-text-heading_large slds-m-bottom_small th-agentforce-assessment__heading"
              data-testid="results-heading"
              tabIndex="-1">
              {isScoreThresholdRequired && !results.scoreThresholdMet
                ? tResults('title_not_quite')
                : tResults('title_complete')}
            </h1>

            <p
              className="slds-text-body_regular slds-m-bottom_large th-agentforce-assessment__text-subtle"
              tabIndex="0"
              data-testid={
                results.scoreThresholdMet
                  ? 'results-description-passed'
                  : isScoreThresholdRequired
                    ? 'results-description-failed-required'
                    : 'results-description-failed-suggested'
              }>
              {results.scoreThresholdMet
                ? tResults('subtitle_passed')
                : isScoreThresholdRequired
                  ? tResults('subtitle_not_quite', { score: passingScore })
                  : tResults('subtitle_not_passing', { score: passingScore })}
            </p>

            <StatsBar
              data-testid="results-score"
              items={[
                { icon: 'custom48', sprite: 'custom', text: tLabels('questions_count', { count: questionCount }) },
                { icon: scoreIcon, text: tLabels('your_score', { score }), color: scoreColor },
                { icon: 'clock', text: tLabels('completed_on', { date: completedDate }) },
              ]} />

            <div className="slds-grid slds-grid_align-center slds-m-top_medium th-agentforce-assessment__cta-group">
              {/* When quiz DOES NOT require a scoring threshold */}
              {!isScoreThresholdRequired && !results.scoreThresholdMet && canRetake && (
                <tds-button variant="neutral" onClick={onRetake} data-testid="results-retake-btn">
                  <span>{tResults('buttons.secondary_optional')}</span>
                </tds-button>
              )}

              {/* When quiz requires a scoring threshold and threshold is NOT met */}
              {isScoreThresholdRequired && !results.scoreThresholdMet && canRetake && (
                <tds-button variant="brand" icon-symbol="refresh" onClick={onRetake} data-testid="results-retake-btn">
                  <span>{tResults('buttons.secondary')}</span>
                </tds-button>
              )}

              {/* Show Return to Trail button when threshold is met OR when threshold is not required and not met */}
              {(results.scoreThresholdMet || (!isScoreThresholdRequired && !results.scoreThresholdMet)) && (
                <tds-button
                  variant="brand"
                  onClick={onBackToTrail}
                  data-testid="results-back-btn">
                  <span>{tResults('buttons.primary')}</span>
                </tds-button>
              )}
            </div>

            <p className="slds-text-body_small slds-m-top_medium">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onInfoClick();
                }}
                className="slds-text-link"
                data-testid="results-learn-more-link">
                {tLabels('learn_more_scores')}
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="slds-container_center slds-container_medium slds-p-around_large">
        {attempt.questions.map((question, index) => (
          <div key={question.quizQuestionId} className="slds-m-bottom_large" data-testid="results-question">
            <div className="slds-grid th-agentforce-assessment__question-row">
              {/* Question Number */}
              <div className="slds-shrink">
                <div
                  className="slds-text-title_bold slds-text-align_center th-agentforce-assessment__question-number"
                  aria-hidden="true">
                  {index + 1}
                </div>
              </div>

              {/* Question Content */}
              <div className="slds-grow">
                <h3 className="th-sr-only" data-testid="results-question-heading" tabIndex="0">
                  {tQuestion("question_label", { number: index + 1 })}
                </h3>
                <p className="slds-text-heading_small tds-color_midnight slds-m-bottom_medium" tabIndex="0">
                  {question.questionText}
                </p>

                {question.response && (
                  <>
                    <p className="slds-text-body_regular tds-text_bold slds-m-bottom_x-small">
                      {tResults('answer_label')}
                    </p>
                    <p className="slds-text-body_regular slds-m-bottom_medium" tabIndex="0">
                      {question.response.userResponse}
                    </p>

                    <FeedbackCard response={question.response} gradeTabbable />
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

ResultsScreen.propTypes = {
  attempt: PropTypes.shape({
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        quizQuestionId: PropTypes.string.isRequired,
        questionText: PropTypes.string.isRequired,
        response: PropTypes.object,
      })
    ).isRequired,
    results: PropTypes.shape({
      overallScore: PropTypes.number.isRequired,
      scoreThresholdMet: PropTypes.bool.isRequired,
      finishedAt: PropTypes.string,
    }).isRequired,
    topic: PropTypes.shape({
      scoreThreshold: PropTypes.number,
      scoreThresholdRequired: PropTypes.bool,
    }),
  }).isRequired,
  canRetake: PropTypes.bool.isRequired,
  onRetake: PropTypes.func.isRequired,
  onBackToTrail: PropTypes.func.isRequired,
  onInfoClick: PropTypes.func.isRequired,
  onFeedbackClick: PropTypes.func,
  showFeedbackButton: PropTypes.bool,
  headingRef: refPropType,
};

export default ResultsScreen;
